// =============================================================================
// Task Assignment Rule Types
// =============================================================================

/**
 * Fields available for assignment rule conditions.
 * These map to task and referral fields.
 */
export type AssignmentRuleField =
  'priority' | 'insurance' | 'provider' | 'diagnoses' | 'services' | 'location' | 'label';

/**
 * Operators available for condition matching.
 */
export type ConditionOperator =
  'equals' | 'not_equals' | 'in' | 'not_in' | 'contains_any' | 'contains_all';

/**
 * Logical connector between conditions in the legacy flat shape.
 *
 * @deprecated Rules are stored as `AssignmentRuleNode` trees; logical
 * grouping is carried by the `op: 'all' | 'any'` field on group
 * nodes. This type remains exported only so the migration adapter in
 * `flatToTree` can read legacy rows during deploy.
 */
export type LogicalConnector = 'AND' | 'OR';

/**
 * A single condition row in the legacy flat condition list.
 *
 * @deprecated Use `AssignmentRuleNode` with `kind: 'condition'`. The
 * engine, the editor, and the API contract all speak
 * `AssignmentRuleNode` now; this shape only feeds `flatToTree` during
 * data migration.
 */
export interface AssignmentRuleConditionRow {
  id: string;
  connector: LogicalConnector;
  field: AssignmentRuleField;
  operator: ConditionOperator;
  value: string | string[];
}

/**
 * Recursive boolean expression tree — the on-disk and over-the-wire
 * shape for assignment rule conditions. A `condition` node is a leaf
 * that evaluates against the `RuleEvaluationContext`; a `group` node
 * combines its children with `op: 'all'` (AND) or `op: 'any'` (OR).
 *
 * Precedence is explicit: the parent group's `op` decides how its
 * children compose. No inline AND/OR connectors at the row level —
 * that was the source of the old flat-list precedence trap, where
 * `[A, AND B, OR C, AND D]` mis-grouped as `(A AND B) OR (C AND D)`
 * instead of what users typically intended.
 */
export type AssignmentRuleNode =
  | {
      kind: 'condition';
      field: AssignmentRuleField;
      operator: ConditionOperator;
      value: string | string[];
    }
  | {
      kind: 'group';
      op: 'all' | 'any';
      children: AssignmentRuleNode[];
    };

/**
 * Maximum allowed nesting depth from the root group. The root counts
 * as depth 1; one level of nested groups makes depth 2, etc. Cap at 5
 * so the author UI stays comprehensible — past depth 5 the prose
 * summary stops being readable as a sentence and the editor becomes
 * a maze.
 */
export const ASSIGNMENT_RULE_MAX_DEPTH = 5;

/**
 * Maximum children per group. Bounds Zod array sizes and prevents
 * pathological rule shapes that would melt the editor or summary.
 */
export const ASSIGNMENT_RULE_MAX_CHILDREN = 20;

/**
 * Context object built from task + referral for rule evaluation.
 */
export interface RuleEvaluationContext {
  priority: string | null;
  locationId: string | null;
  labels: string[];
  insurance: string | null;
  provider: string | null;
  diagnoses: string[];
  services: string[];
}
